import { BaseModel } from './base-model';

export abstract class BaseResourceController<ResourceKey extends string, Model extends BaseModel<ResourceKey>> {
  private readonly resourceKeys: ResourceKey[];
  private readonly seq: Record<ResourceKey, number>;
  private readonly inFlightCount: Record<ResourceKey, number>;
  private disposed = false;

  protected constructor(resourceKeys: readonly ResourceKey[]) {
    this.resourceKeys = [...resourceKeys];
    this.seq = Object.fromEntries(resourceKeys.map((key) => [key, 0])) as Record<ResourceKey, number>;
    this.inFlightCount = Object.fromEntries(resourceKeys.map((key) => [key, 0])) as Record<ResourceKey, number>;
  }

  protected abstract getModel(): Model;

  protected syncLoading(loadingByKey: Record<ResourceKey, boolean>): void {
    this.getModel().setLoadingByKey(loadingByKey);
  }

  protected syncClearError(key: ResourceKey): void {
    this.getModel().clearError(key);
  }

  protected syncError(key: ResourceKey, message: string): void {
    this.getModel().setError(key, message);
  }

  protected isDisposed(): boolean {
    return this.disposed;
  }

  protected beginRequest(key: ResourceKey): number {
    this.seq[key] += 1;
    this.inFlightCount[key] += 1;
    this.syncLoading(this.getLoadingByKey());
    return this.seq[key];
  }

  protected endRequest(key: ResourceKey): void {
    this.inFlightCount[key] = Math.max(0, this.inFlightCount[key] - 1);
    this.syncLoading(this.getLoadingByKey());
  }

  protected isLatest(key: ResourceKey, requestId: number): boolean {
    return this.seq[key] === requestId;
  }

  protected async withRequest(
    key: ResourceKey,
    options: {
      useErrorMessage?: (error: unknown) => string;
      onError?: (error: unknown) => void;
      run: (helpers: { shouldIgnore: () => boolean }) => Promise<void>;
    }
  ): Promise<void> {
    if (this.isDisposed()) {
      return;
    }

    const requestId = this.beginRequest(key);
    const shouldIgnore = () => this.isDisposed() || !this.isLatest(key, requestId);
    this.syncClearError(key);

    try {
      await options.run({ shouldIgnore });
    } catch (error) {
      if (shouldIgnore()) {
        return;
      }

      const normalizedKey = String(key)
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/[_-]+/g, ' ')
        .toLowerCase();
      const message = options.useErrorMessage?.(error) ?? `Failed to load ${normalizedKey}`;
      this.syncError(key, message);
      options.onError?.(error);
    } finally {
      this.endRequest(key);
    }
  }

  public dispose(): void {
    this.disposed = true;
    for (const key of this.resourceKeys) {
      this.seq[key] += 1;
    }
  }

  private getLoadingByKey(): Record<ResourceKey, boolean> {
    const loadingByKey = {} as Record<ResourceKey, boolean>;
    for (const key of this.resourceKeys) {
      loadingByKey[key] = this.inFlightCount[key] > 0;
    }
    return loadingByKey;
  }
}
